import _, { isUndefined } from 'lodash';

import { NO_COMPANY_STATUSES } from 'constants/Experience';
import UserRoles, { isCompanyAdmin, isModerator, isRespondent, isTranslator } from 'constants/UserRoles';
import YEARS, { EXPERIENCE } from 'constants/YearsOfExperience';

export async function validatePersonalBasics(role, data, t, validatePhone) {
    const {
        accountType,
        workIndustry,
        birthday,
        phone,
        languages,
        gender,
        phoneCountryCode,
        firstName,
        lastName,
        nativeCountry,
        workExperience,
    } = data;
    const isCompanyAdmin = role === UserRoles.companyAdmin;
    const isModerator = role === UserRoles.moderator;
    const isTranslator = role === UserRoles.translator;

    const errors = {};
    if (!birthday) {
        errors.birthday = 'welcome:errors.birthday';
    }

    if (!firstName) {
        errors.firstName = 'welcome:errors.firstName';
    }

    if (!lastName) {
        errors.lastName = 'welcome:errors.lastName';
    }

    if (!nativeCountry) {
        errors.nativeCountry = 'welcome:errors.nationality';
    }

    if (!phone.trim() || !phoneCountryCode) {
        errors.phone = 'welcome:errors.phone';
    }

    if (phone.trim()) {
        const result = await validatePhone();
        if (!result) errors.phone = 'welcome:errors.invalidPhone';
    }

    if (!isCompanyAdmin && !gender) {
        errors.gender = 'welcome:errors.gender';
    }
    if ((isModerator || isTranslator) && !workExperience) {
        errors.workExperience = 'welcome:errors.workExperience';
    }
    if ((isModerator || isTranslator) && birthday && workExperience) {
        const age = new Date().getFullYear() - new Date(birthday).getFullYear();
        if (workExperience !== YEARS[0]) {
            const [start] = EXPERIENCE[workExperience];
            if (age < start) {
                errors.workExperience = 'welcome:errors.wrongWorkExperience';
            }
        }
    }
    if (isCompanyAdmin && !workIndustry) {
        errors.workIndustry = 'welcome:errors.workIndustry';
    }
    if (!isTranslator && !isCompanyAdmin && (!languages || !languages.length)) {
        errors.languages = 'welcome:errors.languages';
    }

    validateLength({ accountType, workIndustry, birthday, phone, languages, gender }, errors, t);

    return errors;
}

export function validateAddress({ address, city, state, postalCode, country, nativeCountry }, t) {
    const errors = {};

    if (!city || !city.trim()) {
        errors.city = 'welcome:errors.city';
    }
    if (!state || !state.trim()) {
        errors.state = 'welcome:errors.state';
    }
    if (!postalCode || !postalCode.trim()) {
        errors.postalCode = 'welcome:errors.postalCode';
    }
    if (!country) {
        errors.country = 'welcome:errors.country';
    }
    if (!nativeCountry) {
        errors.nativeCountry = 'welcome:errors.nativeCountry';
    }

    validateLength({ address, city, state, postalCode, country, nativeCountry }, errors, t);

    return errors;
}

export function validateWorkDetails(role, data, t) {
    const { employmentStatus, workExperience, company, occupation, costPerHour, currency } = data;

    const errors = {};
    if (!employmentStatus) {
        errors.employmentStatus = 'welcome:errors.employmentStatus';
    }
    if (!workExperience) {
        errors.workExperience = 'welcome:errors.workExperience';
    }
    if ((!company || !company.trim()) && !NO_COMPANY_STATUSES.includes(employmentStatus)) {
        errors.company = 'welcome:errors.companyName';
    }
    if ((!occupation || !occupation.trim()) && !NO_COMPANY_STATUSES.includes(employmentStatus)) {
        errors.occupation = 'welcome:errors.occupation';
    }

    validateLength({ employmentStatus, workExperience, company, occupation, costPerHour, currency }, errors, t);

    return errors;
}

export function validatePaymentDetails(role, data, t) {
    const { costPerHour, hourlyRate, full, summary } = data;
    const errors = {};
    const isRespondentUser = isRespondent(role);
    const isModeratorUser = isModerator(role);
    const isTranslatorUser = isTranslator(role);

    if (isRespondentUser && !costPerHour) {
        // errors.costPerHour = 'welcome:errors.costPerHour';
    }
    if (isTranslatorUser || isModeratorUser) {
        if (isUndefined(hourlyRate['offline-home'])) {
            errors['offline-home'] = 'welcome:errors.offline-home';
        }
        if (isUndefined(hourlyRate['vfgd'])) {
            errors['vfgd'] = 'welcome:errors.vfgd';
        }
        if (isUndefined(hourlyRate['video-idi'])) {
            errors['video-idi'] = 'welcome:errors.video-idi';
        }
        if (isUndefined(hourlyRate['offline-focus-group'])) {
            errors['offline-focus-group'] = 'welcome:errors.offline-focus-group';
        }
        if (isUndefined(hourlyRate['offline-interview'])) {
            errors['offline-interview'] = 'welcome:errors.offline-interview';
        }

        if (isModeratorUser) {
            if (isUndefined(hourlyRate['online'])) {
                errors['online'] = 'welcome:errors.online';
            }
            if (isUndefined(full)) {
                errors.full = 'welcome:errors.full';
            }

            if (isUndefined(summary)) {
                errors.summary = 'welcome:errors.summary';
            }
        }
    }

    return errors;
}

export function validateTranslation({ translateLanguages }, t) {
    const errors = {};

    const isEqualObjectsInLanguageArray = () => {
        for (let i = 0; i < translateLanguages.length - 1; ++i) {
            for (let j = i + 1; j < translateLanguages.length; ++j) {
                if (_.isEqual(translateLanguages[i], translateLanguages[j])) {
                    errors.translate = 'welcome:errors.translateDuplicates';
                    return;
                }
            }
        }
    };

    if (translateLanguages.length >= 2) {
        isEqualObjectsInLanguageArray();
    }

    for (let languagePair of translateLanguages) {
        if (!languagePair.from || !languagePair.to) {
            errors.translate = 'welcome:errors.translateAllFields';
            break;
        }
    }

    if (translateLanguages.length === 0) {
        errors.translate = 'welcome:errors.noLanguages';
    }

    return errors;
}

export function validateAboutCompany(role, { name, type, vat, companyIndustry }, t, hasVat) {
    const errors = {};
    if (!name || !name.trim()) {
        errors.companyName = 'welcome:errors.companyName';
    }
    if (!type) {
        errors.companyType = 'welcome:errors.companyType';
    }
    if (isCompanyAdmin(role) && !companyIndustry) {
        errors.companyIndustry = 'welcome:errors.companyType';
    }

    validateLength(
        {
            companyName: name,
            companyType: type,
            companyIndustry: companyIndustry,
        },
        errors,
        t,
    );

    return errors;
}

export async function validateRepresentative(role, data, t, validatePhone, otherRole) {
    const {
        birthday,
        phone,
        languages,
        phoneCountryCode,
        firstName,
        lastName,
        businessRole,
        nativeCountry,
        workExperience,
    } = data;
    const errors = {};
    const isTranslatorOrModerator = isTranslator(role) || isModerator(role);

    if (!birthday) {
        errors.birthday = 'welcome:errors.birthday';
    }

    if (!firstName) {
        errors.firstName = 'welcome:errors.firstName';
    }

    if (!lastName) {
        errors.lastName = 'welcome:errors.lastName';
    }

    if (!phone.trim() || !phoneCountryCode) {
        errors.phone = 'welcome:errors.phone';
    }

    if (phone.trim()) {
        const result = await validatePhone();
        if (!result) errors.phone = 'welcome:errors.invalidPhone';
    }
    if (!nativeCountry) {
        errors.nativeCountry = 'welcome:errors.nationality';
    }
    if (businessRole === 'other' && !otherRole) {
        errors.businessRole = 'welcome:errors.businessRole';
    }

    if (!isTranslator(role) && !isCompanyAdmin(role) && (!languages || !languages.length)) {
        errors.languages = 'welcome:errors.languages';
    }

    if (isTranslatorOrModerator && !workExperience) {
        errors.workExperience = 'welcome:errors.workExperience';
    }

    validateLength({ birthday, phone, languages }, errors, t);

    return errors;
}

export function validateCompanyAddress({ house, street, city, state, postalCode, country }, t) {
    const errors = {};
    if (!city || !city.trim()) {
        errors.companyCity = 'welcome:errors.city';
    }
    if (!state || !state.trim()) {
        errors.companyState = 'welcome:errors.state';
    }
    if (!postalCode || !postalCode.trim()) {
        errors.companyPostalCode = 'welcome:errors.postalCode';
    }
    if (!country) {
        errors.companyCountry = 'welcome:errors.country';
    }
    if (!street || !street.trim()) {
        errors.companyStreet = 'welcome:errors.street';
    }
    if (!house || !house.trim()) {
        errors.companyHouse = 'welcome:errors.house';
    }

    validateLength(
        {
            companyCity: city,
            companyState: state,
            companyPostalCode: postalCode,
            companyCountry: country,
            companyStreet: street,
            companyHouse: house,
        },
        errors,
        t,
    );

    return errors;
}

export const validateMedicalData = ({ speciality, subSpeciality, healthCareFacility, experience }, t) => {
    const errors = {};

    if (!speciality) {
        errors.speciality = 'welcome:errors.speciality';
    }

    if (!subSpeciality) {
        errors.subSpeciality = 'welcome:errors.subSpeciality';
    }

    if (!healthCareFacility) {
        errors.healthCareFacility = 'welcome:errors.healthCareFacility';
    }

    if (!experience) {
        errors.experience = 'welcome:errors.experience';
    }

    return errors;
};

export const validateBusinessData = (data, t) => {
    const errors = {};

    if (!data.companyName || !data.companyName.trim()) {
        errors.companyName = 'welcome:errors.companyName';
    }

    if (!data.companyType) {
        errors.companyType = 'welcome:errors.companyType';
    }

    if (!data.industry) {
        errors.industry = 'welcome:errors.industry';
    }

    if (!data.companyResponsibilities) {
        errors.companyResponsibilities = 'welcome:errors.companySize';
    }

    if (!data.companyAddress || !data.companyAddress.trim()) {
        errors.companyAddress = 'welcome:errors.companyAddress';
    }

    if (!data.revenue || !data.revenue.trim()) {
        errors.revenue = 'welcome:errors.revenue';
    }

    if (!data.currency) {
        errors.currency = 'welcome:errors.currency';
    }

    if (!data.companyPosition) {
        errors.companyPosition = 'welcome:errors.companyPosition';
    }

    if (!data.companyDivision) {
        errors.companyDivision = 'welcome:errors.companyDivision';
    }

    validateLength(data, errors, t);

    return errors;
};

export const validateLength = (data, errors, t) => {
    for (let key in data) {
        if (data[key] && data[key].length > 255) {
            errors[key] = `${t(`welcome:text.${key}`)} ${t('welcome:errors.length')}`;
        }
    }
};

const arrayFields = ['languages'];
const hourlyRateFields = ['offline-home', 'vfgd', 'offline-interview', 'video-idi', 'offline-focus-group', 'online'];
export const validateSingleField = (data, field) => {
    if (arrayFields.includes(field)) {
        if (!data[field] || !data[field].length) {
            return `welcome:errors.${field}`;
        }
        return null;
    }
    if (field.includes('company')) {
        if (!data[field.substring(7).toLowerCase()]) return `welcome:errors.${field}`;
        return null;
    }
    if (hourlyRateFields.includes(field)) {
        if (!data.hourlyRate[field]) {
            return `welcome:errors.${field}`;
        }
        return null;
    }
    if (field === 'businessRole') {
        return null;
    }
    if (!data[field]) {
        return `welcome:errors.${field}`;
    }
    return null;
};
